////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * SIGN-IN POD - AUTHENTICATION ORCHESTRATOR (POD/PEA PATTERN)
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: The Pod (orchestrator) handles business logic, state management,
 * API calls, and validation for the sign-in flow, delegating presentation to the Pea.
 *
 * POD/PEA PATTERN:
 *   - POD (THIS FILE): State, logic, API integration, navigation
 *   - PEA (PARTNER FILE): Pure presentation, UI rendering, event callbacks
 *
 * I[R{DVT}]O PATTERN ANALYSIS:
 *
 * INPUT (I):
 *   - User email and password from sign-in form (via Pea callbacks)
 *   - Remember me checkbox state
 *   - Navigation events (forgot password, register clicks)
 *
 * REQUIREMENTS (R):
 *
 *   DEFINITIONS (D):
 *     - SignInFormData: Interface for email, password, rememberMe fields
 *     - errors: Record<string, string> for field-level validation messages
 *     - isLoading: boolean for API call state management
 *
 *   VALIDATIONS (V):
 *     - Email format validation (RFC 5322 pattern: /\S+@\S+\.\S+/)
 *     - Password presence validation (required field check)
 *     - Response validation from auth backend API
 *
 *   TRANSFORMATIONS (T):
 *     - Form data to API request payload
 *     - API response to localStorage tokens
 *     - Success response to Account Knight navigation
 *     - Error response to user-friendly error messages
 *
 * OUTPUT (O):
 *   - SUCCESS: JWT token in localStorage, redirect to Account dashboard
 *   - SUCCESS: Customer ID and company name stored for session
 *   - FAILURE: Validation error messages displayed in form
 *   - FAILURE: API error messages shown to user
 *
 * SECURITY OBLIGATIONS:
 *   - Passwords never logged or stored unencrypted
 *   - JWT tokens stored only in localStorage (httpOnly not available client-side)
 *   - Failed login attempts should be rate-limited (future enhancement)
 *   - HTTPS required in production for credential transmission
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Pea_Lobby_SignIn_SRC_Auth_Customer_Kings from '../pea/Bitsbound_Kings_Customer_Auth_SRC_SignIn_Lobby_Pea.js';

/**
 * DEFINITION (D): SignInFormData interface defines the structure of sign-in form state
 * Ensures type safety for email, password, remember me checkbox, and 2FA code
 */
interface SignInFormData {
  email: string;        // DEFINITION: User's email address (serves as username)
  password: string;     // DEFINITION: User's password (never logged or stored unencrypted)
  rememberMe: boolean;  // DEFINITION: Whether to persist session for 30 days
  twoFactorCode: string; // DEFINITION: 6-digit TOTP code for 2FA verification
}

/**
 * TRANSFORMATION (T): Sign-In Pod component orchestrates authentication logic
 * Manages form state, validation, API communication, and navigation flow
 *
 * INPUT: User interactions via Pea component callbacks
 * OUTPUT: Authenticated session or validation errors
 */
const Pod_Lobby_SignIn_SRC_Auth_Customer_Kings: React.FC = () => {
  // DEFINITION: React Router navigation hook for programmatic routing
  const navigate = useNavigate();

  // DEFINITION: Form state management for email, password, remember me, 2FA code
  const [formData, setFormData] = useState<SignInFormData>({
    email: '',           // INPUT: User-entered email address
    password: '',        // INPUT: User-entered password
    rememberMe: false,   // INPUT: Session persistence preference
    twoFactorCode: ''    // INPUT: 6-digit TOTP code for 2FA verification
  });

  // DEFINITION: 2FA state - when true, show 2FA code entry instead of password
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);

  // DEFINITION: Error state for field-level validation messages
  const [errors, setErrors] = useState<Record<string, string>>({});

  // DEFINITION: Loading state for API call in progress
  const [isLoading, setIsLoading] = useState(false);

  // DEFINITION: Success message state for post-verification
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // T_URL_ERROR_HANDLER: Check for error parameters in URL on mount
  // TIR{ADVT}O: OAuth callbacks redirect here with error codes that need user-friendly messages
  // T_VERIFIED_HANDLER_2026_01_02: Also handle verified=true from email verification redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get('error');
    const verified = urlParams.get('verified');
    const email = urlParams.get('email');

    // T_VERIFIED_SUCCESS_2026_01_02: Handle successful email verification redirect
    if (verified === 'true') {
      setSuccessMessage('Email verified successfully! You can now sign in.');
      // Pre-fill email if provided
      if (email) {
        setFormData(prev => ({ ...prev, email: decodeURIComponent(email) }));
      }
      // Clean URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('verified');
      newUrl.searchParams.delete('email');
      window.history.replaceState({}, '', newUrl.pathname);
      console.log('✅ Email verification successful, user can now sign in');
    }

    if (errorCode) {
      // DEFINITION: Map error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        'account_deleted': 'This account has been deleted and cannot be used to sign in. Please create a new account with a different email address.',
        'oauth_denied': 'Google sign-in was cancelled or denied. Please try again.',
        'invalid_callback': 'Invalid sign-in response. Please try again.',
        'callback_error': 'An error occurred during sign-in. Please try again.',
        'email_not_verified': 'Your email must be verified by Google to sign in.'
      };

      // TRANSFORMATION: Set error message based on error code
      const message = errorMessages[errorCode] || 'An error occurred. Please try again.';
      setErrors(prev => ({ ...prev, submit: message }));

      // TRANSFORMATION: Clean URL by removing error param (better UX)
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, '', newUrl.pathname);

      console.log('🚫 Sign-in error from URL:', errorCode);
    }
  }, []);

  /**
   * TRANSFORMATION (T): Initiates Google OAuth flow by redirecting to backend
   * The backend handles the OAuth dance and redirects back with JWT token
   *
   * INPUT: User click on Google sign-in button
   * OUTPUT: Browser redirect to Google consent screen via backend
   *
   * FLOW:
   *   1. Build auth backend URL with optional invitation params
   *   2. Redirect browser to /api/auth/google endpoint
   *   3. Backend generates state, redirects to Google
   *   4. User consents → Google redirects to callback
   *   5. Backend exchanges code → creates session → redirects to dashboard
   */
  const handleGoogleSignIn = () => {
    // 📊 GA4 FUNNEL TRACKING: Track Google sign-in initiation (actual login tracked after OAuth callback)
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'login_start', {
        method: 'google'
      });
      console.log('📊 GA4: Google login_start event tracked');
    }

    // TRANSFORMATION: Environment-aware auth backend URL
    // VALIDATION: Check for production hostnames (main domain OR direct Vercel app access)
    const hostname = window.location.hostname;
    const isProduction = hostname === 'www.bitsbound.com' ||
                        hostname === 'bitsbound.com' ||
                        hostname.includes('vercel.app');
    const authBackendUrl = isProduction
      ? 'https://bitsbound-auth-backend.vercel.app'  // DEFINITION: Production auth backend
      : 'http://localhost:2102';                      // DEFINITION: Local development (port 2102)

    // TRANSFORMATION: Build Google OAuth initiation URL
    const googleAuthUrl = new URL(`${authBackendUrl}/api/auth/google`);

    // TRANSFORMATION: Preserve invitation context if present (for invitation flow)
    const urlParams = new URLSearchParams(window.location.search);
    const invitationToken = urlParams.get('token');
    const invitationRoomId = urlParams.get('roomId');
    const redirectPath = urlParams.get('redirect');

    if (invitationToken) {
      googleAuthUrl.searchParams.set('token', invitationToken);
    }
    if (invitationRoomId) {
      googleAuthUrl.searchParams.set('roomId', invitationRoomId);
    }
    if (redirectPath) {
      googleAuthUrl.searchParams.set('redirect', redirectPath);
    }

    console.log('🔐 Initiating Google OAuth flow:', googleAuthUrl.toString());

    // OUTPUT: Redirect to Google OAuth via backend
    window.location.href = googleAuthUrl.toString();
  };

  /**
   * VALIDATION (V): Validates sign-in form data before API submission
   * Implements client-side validation for email format and required fields
   *
   * INPUT: Current formData state
   * OUTPUT: boolean (true if valid, false if errors exist)
   * SIDE EFFECT: Updates errors state with validation messages
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // VALIDATION: Email presence check
    if (!formData.email) {
      newErrors.email = 'Email is required';  // OUTPUT: Required field error
    }
    // VALIDATION: Email format check (RFC 5322 simplified pattern)
    else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';  // OUTPUT: Format validation error
    }

    // VALIDATION: Password presence check
    if (!formData.password) {
      newErrors.password = 'Password is required';  // OUTPUT: Required field error
    }

    // TRANSFORMATION: Update error state with validation results
    setErrors(newErrors);

    // OUTPUT: Return validation result (true = no errors, false = errors exist)
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * TRANSFORMATION (T): Handles form submission and authentication API call
   * Orchestrates validation, API request, token storage, and navigation
   *
   * INPUT: Form submit event
   * OUTPUT: Authenticated session or error messages
   * SIDE EFFECTS: Updates localStorage, navigates to Account Knight
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();  // VALIDATION: Prevent default form submission

    // VALIDATION: Check form validity before API call
    if (!validateForm()) {
      return;  // OUTPUT: Exit early if validation fails
    }

    setIsLoading(true);  // TRANSFORMATION: Set loading state for UI feedback

    try {
      // TRANSFORMATION: Environment-aware auth backend URL
      // VALIDATION: Check for production hostnames (main domain OR direct Vercel app access)
      const hostname = window.location.hostname;
      const isProduction = hostname === 'www.bitsbound.com' ||
                          hostname === 'bitsbound.com' ||
                          hostname.includes('vercel.app');
      const authBackendUrl = isProduction
        ? 'https://bitsbound-auth-backend.vercel.app'  // DEFINITION: Production auth backend
        : 'http://localhost:2102';                      // DEFINITION: Local development (port 2102)

      // TRANSFORMATION: Call authentication API with credentials
      // T_2FA: Include twoFactorCode if we're in the 2FA verification step
      const response = await fetch(`${authBackendUrl}/api/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',  // DEFINITION: JSON payload
        },
        body: JSON.stringify({
          email: formData.email,              // INPUT: User email
          password: formData.password,        // INPUT: User password (transmitted over HTTPS in production)
          rememberMe: formData.rememberMe,    // INPUT: Session persistence preference
          twoFactorCode: requiresTwoFactor ? formData.twoFactorCode : undefined  // INPUT: 2FA code if required
        })
      });

      // TRANSFORMATION: Parse JSON response from backend
      const data = await response.json();

      // T_2FA_CHECK: Handle 2FA requirement from backend
      // When 2FA is enabled, backend returns { success: false, requiresTwoFactor: true, customerId: '...' }
      if (data.requiresTwoFactor && !requiresTwoFactor) {
        console.log('🔐 2FA Required for account:', data.customerId);
        setRequiresTwoFactor(true);  // TRANSFORMATION: Switch UI to 2FA code entry mode
        setIsLoading(false);
        setErrors({});  // TRANSFORMATION: Clear any previous errors
        return;  // OUTPUT: Exit and wait for user to enter 2FA code
      }

      // V_EMAIL_VERIFICATION: Handle unverified email error (HTTP 403)
      // T_FIX_ERROR_DISPLAY_2026_01_02: Use 'submit' key so error displays in form-level error UI
      if (response.status === 403 && data.requiresVerification) {
        console.log('📧 Email not verified for account:', data.email);
        setIsLoading(false);
        setErrors({
          submit: 'Please verify your email address before signing in. Check your inbox for the verification link.'
        });
        return;
      }

      // VALIDATION: Check if authentication succeeded
      if (response.ok && data.success) {
        // 📊 GA4 FUNNEL TRACKING: Login event + user_id for cross-device tracking
        if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
          // Track login event (GA4 recommended event)
          (window as any).gtag('event', 'login', {
            method: 'email'
          });
          // Set user_id for cross-device/cross-session tracking
          (window as any).gtag('config', 'G-1LSNGFSBEF', {
            user_id: data.customerId
          });
          console.log('📊 GA4: login event tracked for user:', data.customerId);
        }

        // 🔥 DEBUG: Log what we received from backend
        console.log('🔍 [SIGN-IN DEBUG] Backend response data:', {
          token: data.token ? '(present)' : '(MISSING)',
          customerId: data.customerId,
          customerEmail: data.customer?.email,
          formDataEmail: formData.email,
          companyName: data.customer?.companyName,
          normalizedCompanyName: data.customer?.normalizedCompanyName
        });

        // 🔥 CRITICAL FIX (2025-10-14): Clear ALL localStorage before setting new values
        // This prevents stale data from previous sessions (e.g., old customerId, companyName)
        // causing identity confusion in Negotiations Room
        localStorage.clear(); // 🧹 Clear all localStorage entries
        console.log('🧹 Cleared all localStorage entries before new sign-in');

        // TRANSFORMATION: Store JWT token in localStorage for session management
        localStorage.setItem('authToken', data.token);  // OUTPUT: Store auth token
        console.log('🔍 [AFTER SET] authToken in localStorage:', localStorage.getItem('authToken') ? '(present)' : '(MISSING)');

        localStorage.setItem('customerId', data.customerId);  // OUTPUT: Store customer ID
        console.log('🔍 [AFTER SET] customerId in localStorage:', localStorage.getItem('customerId'));

        const userEmailValue = data.customer?.email || formData.email;
        localStorage.setItem('userEmail', userEmailValue);  // OUTPUT: Store user email for invitation validation
        console.log('🔍 [AFTER SET] userEmail in localStorage:', localStorage.getItem('userEmail'));
        console.log('🔍 [AFTER SET] userEmail value that was set:', userEmailValue);

        // TRANSFORMATION: Store company name if provided
        if (data.customer?.companyName) {
          localStorage.setItem('companyName', data.customer.companyName);  // OUTPUT: Store company name
          console.log('🔍 [AFTER SET] companyName in localStorage:', localStorage.getItem('companyName'));
        }

        // TRANSFORMATION: Store normalized company name for URL routing
        const normalizedCompanyName = data.customer?.normalizedCompanyName || 'unnamed';
        localStorage.setItem('normalizedCompanyName', normalizedCompanyName);  // OUTPUT: Store URL-safe company name
        console.log('🔍 [AFTER SET] normalizedCompanyName in localStorage:', localStorage.getItem('normalizedCompanyName'));

        console.log('✅ New localStorage values set:', {
          authToken: '(token hidden)',
          customerId: data.customerId,
          userEmail: data.customer?.email || formData.email,
          companyName: data.customer?.companyName,
          normalizedCompanyName
        });

        // 🔥 DEBUG: Verify ALL localStorage after all sets
        console.log('🔍 [FINAL CHECK] All localStorage keys:', Object.keys(localStorage));
        console.log('🔍 [FINAL CHECK] All localStorage values:', {...localStorage});

        // 🔥 CHECK FOR INVITATION REDIRECT (2025-10-12): If user is signing in from invitation link
        const urlParams = new URLSearchParams(window.location.search);
        const invitationRedirect = urlParams.get('redirect'); // e.g., "/negotiations-room"
        const invitationToken = urlParams.get('token'); // e.g., "inv_..."
        const invitationRoomId = urlParams.get('roomId'); // e.g., "room_123..."

        if (invitationRedirect && invitationToken && invitationRoomId) {
          // 🎯 INVITATION FLOW: User clicked invitation link, signed in, now redirect back to negotiations room with token
          console.log('🔥 INVITATION SIGN-IN DETECTED!');
          console.log('   Redirect Target:', invitationRedirect);
          console.log('   Invitation Token:', invitationToken.substring(0, 10) + '...');
          console.log('   Room ID:', invitationRoomId);

          // BUILD REDIRECT URL: Codifications SaaS frontend with invitation token preserved (hardcoded port 2201)
          const codificationsBaseUrl = import.meta.env.MODE === 'production'
            ? 'https://bitsbound.com'  // DEFINITION: Production unified frontend
            : 'http://localhost:2201';  // DEFINITION: Local development URL (port 2201)

          // FORMAT: /customer-name/room-id/negotiations-room?token=inv_...&authToken=jwt_...
          // 🔥 CRITICAL: Pass authToken in URL because localStorage is domain-specific (port 2101 ≠ port 2201)!
          const invitationRedirectUrl = `${codificationsBaseUrl}/${normalizedCompanyName}/${invitationRoomId}/negotiations-room?token=${encodeURIComponent(invitationToken)}&authToken=${encodeURIComponent(data.token)}`;

          console.log('✅ Sign-in successful! Redirecting to invitation negotiations room...');
          console.log('   🚀 Redirect URL:', invitationRedirectUrl);
          console.log('   🔑 Passing authToken in URL (localStorage is port-specific!)');

          // OUTPUT: Redirect back to negotiations room with invitation token AND authToken
          window.location.href = invitationRedirectUrl;
          return; // Exit early - invitation flow handled
        }

        // 📊 DEFAULT FLOW: Normal sign-in without invitation
        // TRANSFORMATION: Navigate to Account Knight frontend (cross-app navigation)
        // DEFINITION: Environment-aware redirect (localhost in dev, production URL in prod)
        const params = new URLSearchParams({
          token: data.token,
          customerId: data.customerId,
          companyName: data.customer?.companyName || '',
          email: data.customer?.email || ''
        });

        // VALIDATION: Determine environment and construct appropriate redirect URL
        // VALIDATION: Check for production hostnames (main domain OR direct Vercel app access)
        const signInHostname = window.location.hostname;
        const isProductionSignIn = signInHostname === 'www.bitsbound.com' ||
                                   signInHostname === 'bitsbound.com' ||
                                   signInHostname.includes('vercel.app');

        // TRANSFORMATION: Construct production or development redirect URL
        // CRITICAL: Production routes through bitsbound.com/_customer-account/ proxy (NOT direct Vercel app)
        const baseUrl = isProductionSignIn
          ? 'https://www.bitsbound.com/_customer-account'  // DEFINITION: Production via main domain proxy
          : 'http://localhost:2103';  // DEFINITION: Local development URL (port 2103)

        // TRANSFORMATION: Use backend's redirectTo path with customer-specific routing
        const accountPath = data.redirectTo || '/account/dashboard';  // DEFINITION: Backend determines onboarding step
        // CRITICAL: Customer-specific URL format: /customer-name/account/dashboard (like GitHub: /username/repos)
        const customerSpecificPath = `/${normalizedCompanyName}${accountPath}`;
        const redirectPath = `${baseUrl}${customerSpecificPath}?${params.toString()}`;

        console.log('✅ Sign-in successful! Redirecting to customer-specific account...');
        console.log('   Token:', data.token);
        console.log('   Customer ID:', data.customerId);
        console.log('   Customer Name:', data.customer?.companyName);
        console.log('   Normalized Name (URL):', normalizedCompanyName);
        console.log('   🚀 Redirect URL:', redirectPath);

        // OUTPUT: Full page navigation to customer-specific Account dashboard with auth data
        window.location.href = redirectPath;
      } else {
        // OUTPUT: Display authentication failure error
        setErrors({ submit: data.error || 'Sign in failed. Please check your credentials.' });
      }
    } catch (error) {
      console.error('Sign in error:', error);  // OUTPUT: Log network/API error
      // OUTPUT: Display user-friendly network error message
      setErrors({ submit: 'Unable to connect to the server. Please try again.' });
    } finally {
      setIsLoading(false);  // TRANSFORMATION: Clear loading state regardless of outcome
    }
  };

  /**
   * TRANSFORMATION (T): Handles field value changes and clears field-specific errors
   * Provides real-time error clearing for improved UX
   *
   * INPUT: Field name and new value
   * OUTPUT: Updated form state and cleared field errors
   */
  const handleFieldChange = (field: keyof SignInFormData, value: string | boolean) => {
    // TRANSFORMATION: Update form data with new value
    setFormData(prev => ({ ...prev, [field]: value }));

    // TRANSFORMATION: Clear field-specific error when user starts typing
    if (field === 'email' && errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));  // OUTPUT: Clear email error
    }
    if (field === 'password' && errors.password) {
      setErrors(prev => ({ ...prev, password: '' }));  // OUTPUT: Clear password error
    }
    // T_2FA: Clear 2FA error when user enters code
    if (field === 'twoFactorCode' && errors.twoFactorCode) {
      setErrors(prev => ({ ...prev, twoFactorCode: '' }));  // OUTPUT: Clear 2FA code error
    }
  };

  /**
   * TRANSFORMATION (T): Handler to go back from 2FA to password entry
   * Allows user to start over if they need to use a different account
   *
   * INPUT: User click on "Use different account" button
   * OUTPUT: Reset to initial sign-in state
   */
  const handleBack2FA = () => {
    setRequiresTwoFactor(false);  // TRANSFORMATION: Exit 2FA mode
    setFormData(prev => ({ ...prev, twoFactorCode: '', password: '' }));  // TRANSFORMATION: Clear sensitive data
    setErrors({});  // TRANSFORMATION: Clear any errors
  };

  /**
   * TRANSFORMATION (T): Navigates to forgot password flow
   * INPUT: User click on forgot password link
   * OUTPUT: Route change to password reset page
   */
  const handleForgotPassword = () => {
    navigate('/auth/forget-password');  // TRANSFORMATION: Programmatic navigation
  };

  /**
   * TRANSFORMATION (T): Navigates to registration flow
   * INPUT: User click on register link
   * OUTPUT: Route change to registration page
   */
  const handleRegisterClick = () => {
    navigate('/auth/register');  // TRANSFORMATION: Programmatic navigation
  };

  // OUTPUT: Render Pea component with all props and callbacks
  return (
    <Pea_Lobby_SignIn_SRC_Auth_Customer_Kings
      formData={formData}                      // INPUT: Current form state
      isLoading={isLoading}                    // INPUT: Loading indicator state
      error={errors.submit}                    // INPUT: Form-level error message
      successMessage={successMessage}          // T_VERIFIED_2026_01_02: Success message for email verification
      onFieldChange={handleFieldChange}        // CALLBACK: Field change handler
      onSubmit={handleSubmit}                  // CALLBACK: Form submission handler
      onForgotPassword={handleForgotPassword}  // CALLBACK: Forgot password navigation
      onRegisterClick={handleRegisterClick}    // CALLBACK: Register navigation
      onGoogleSignIn={handleGoogleSignIn}      // CALLBACK: Google OAuth initiation (server-side flow)
      requiresTwoFactor={requiresTwoFactor}    // T_2FA: Whether 2FA code entry is needed
      onBack2FA={handleBack2FA}                // T_2FA: Callback to go back from 2FA to password
    />
  );
};

export default Pod_Lobby_SignIn_SRC_Auth_Customer_Kings;